import React, { useEffect, useState } from 'react';

const getYouTubeId = (url) => {
  const match = (url || '').match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
  return match ? match[1] : null;
};

const authHeaders = (json = false) => {
  const headers = json ? { 'Content-Type': 'application/json' } : {};
  const token = localStorage.getItem('session_token');
  if (token) headers['X-Session-Token'] = token;
  return headers;
};

const ReplaceVideoForm = ({ exerciseName, backendUrl, isDarkMode, onReplaced }) => {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${backendUrl}/api/exercises/${encodeURIComponent(exerciseName)}/video`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(true), body: JSON.stringify({ video_url: link })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not save video');
      onReplaced(data.video_url);
      setOpen(false);
      setLink('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="video-replace-toggle-btn" onClick={() => setOpen(true)}
        className={`mt-3 text-xs font-medium underline ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
        Wrong video? Replace
      </button>
    );
  }
  return (
    <div className="mt-3 space-y-2" data-testid="video-replace-form">
      <input data-testid="video-replace-input" value={link} onChange={(e) => setLink(e.target.value)}
        placeholder="Paste a YouTube link"
        className={`w-full px-3 py-2 text-sm rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
      {error && <p data-testid="video-replace-error" className="text-xs text-red-500">{error}</p>}
      <div className="flex gap-2">
        <button data-testid="video-replace-save-btn" onClick={save} disabled={!link || saving}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white disabled:opacity-50">
          {saving ? 'Saving...' : 'Save for this exercise'}
        </button>
        <button data-testid="video-replace-cancel-btn" onClick={() => setOpen(false)}
          className={`px-3 py-1.5 text-xs rounded-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Cancel</button>
      </div>
    </div>
  );
};

const VideoBody = ({ videoUrl, exerciseName, backendUrl, isDarkMode }) => {
  if (!videoUrl) {
    return <div data-testid="video-loading" className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Finding video...</div>;
  }
  const ytId = getYouTubeId(videoUrl);
  if (ytId) {
    return (
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe data-testid="video-embed" className="absolute inset-0 w-full h-full rounded-lg"
          src={`https://www.youtube.com/embed/${ytId}?playsinline=1&rel=0`} title={`${exerciseName} video`}
          frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (!videoUrl.includes('youtube.com/results')) {
    const src = videoUrl.startsWith('http') ? videoUrl : `${backendUrl}${videoUrl}`;
    return <video data-testid="video-player" src={src} controls playsInline className="w-full rounded-lg" />;
  }
  return (
    <div className="text-center py-8">
      <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>No video found. Search on YouTube:</p>
      <a data-testid="video-search-link" href={videoUrl} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
        Search on YouTube
      </a>
    </div>
  );
};

export const ExerciseVideoModal = ({ exerciseName, attachedUrl = '', backendUrl, isDarkMode, canReplace = false, onClose }) => {
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    setVideoUrl(null);
    const params = attachedUrl ? `?attached=${encodeURIComponent(attachedUrl)}` : '';
    fetch(`${backendUrl}/api/exercises/${encodeURIComponent(exerciseName)}/video${params}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setVideoUrl(d.video_url))
      .catch(() => setVideoUrl(`https://www.youtube.com/results?search_query=${encodeURIComponent(`${exerciseName} exercise form tutorial`)}`));
  }, [exerciseName, attachedUrl, backendUrl]);

  return (
    <div data-testid="exercise-video-modal" className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
        <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="min-w-0">
            <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Video Guide</h2>
            <p data-testid="video-exercise-name" className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{exerciseName}</p>
          </div>
          <button data-testid="video-modal-close-btn" onClick={onClose}
            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4">
          <VideoBody videoUrl={videoUrl} exerciseName={exerciseName} backendUrl={backendUrl} isDarkMode={isDarkMode} />
          {canReplace && (
            <ReplaceVideoForm exerciseName={exerciseName} backendUrl={backendUrl} isDarkMode={isDarkMode} onReplaced={setVideoUrl} />
          )}
        </div>
      </div>
    </div>
  );
};
